import {
  ArrowLeftRight,
  BarChart3,
  Briefcase,
  CheckCircle2,
  Database,
  FileSignature,
  FileText,
  Link2,
  Palette,
  PenLine,
  ScanText,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * What the welcome tour says, page by page.
 *
 * EVERY LINE HERE DESCRIBES SOMETHING THAT SHIPS. A tour is read once, by
 * someone who has no way yet to tell a real feature from a promise, and who
 * will go looking for whatever it names. So each claim was checked against the
 * screen it points at before it was written, and the wording follows the labels
 * on those screens — "Soteria AI", "Clients", "Your own KYC forms" — so that
 * what the tour says is what the sidebar says. If a screen is renamed, or a
 * feature is taken away, this file is the one that has to move with it.
 *
 * Kept apart from the component so the words can be read, and changed, without
 * reading through the layout, and so the tests can assert against the same
 * titles the dialog renders.
 */

/** The three icons drawn on a page's illustration: one large, two small. */
export interface TourArt {
  main: LucideIcon;
  left: LucideIcon;
  right: LucideIcon;
}

export interface TourPoint {
  icon: LucideIcon;
  text: string;
}

export interface TourPage {
  id: string;
  /** A word or two above the title, naming where in the app this lives. */
  eyebrow: string;
  title: string;
  body: string;
  points: Array<TourPoint>;
  art: TourArt;
  /**
   * A button that takes the reader to the thing just described. Only the
   * Settings page has one: it is the one page describing something the reader
   * has to go and DO, rather than something they will meet on their own.
   */
  action?: { label: string; href: string };
}

/** Open the dedicated forms view, rather than making an admin hunt for it. */
export const FIRM_FORMS_HREF = '/settings?tab=forms';

export const TOUR_PAGES: Array<TourPage> = [
  {
    id: 'welcome',
    eyebrow: 'Welcome',
    title: 'Welcome to BIASafe',
    body: 'Here is a quick look at where everything lives. Five short pages, and you can skip at any time.',
    points: [
      {
        icon: Sparkles,
        text: 'Soteria AI is your assistant. Ask it to find a client, add one, open an account or prep you for a meeting.',
      },
      {
        icon: Sparkles,
        text: 'Open it from any page with the sparkle button at the top, or from Soteria AI in the sidebar.',
      },
    ],
    art: { main: Sparkles, left: Users, right: BarChart3 },
  },
  {
    id: 'clients',
    eyebrow: 'Clients',
    title: 'Onboard clients and open accounts',
    body: 'Everything about a client lives on their page under Clients, including the accounts you open for them.',
    points: [
      {
        icon: Link2,
        text: 'Send a secure onboarding link. The client verifies their identity and answers suitability on their own time.',
      },
      {
        icon: FileSignature,
        text: 'Open accounts at NBIN or Interactive Brokers. For NBIN, the forms package is built from the client’s answers and signed in one go.',
      },
      {
        icon: ArrowLeftRight,
        text: 'Bring existing accounts over with a transfer from the client’s current firm.',
      },
    ],
    art: { main: UserPlus, left: Link2, right: FileSignature },
  },
  {
    id: 'invest',
    eyebrow: 'Strategies, Portfolios, Execution',
    title: 'From strategy to trade',
    body: 'Three places in the sidebar take you from an idea to the orders that carry it out.',
    points: [
      {
        icon: Database,
        text: 'Strategies: build a model, backtest it, read the report, then run it live.',
      },
      {
        icon: Briefcase,
        text: 'Portfolios: holdings, positions and performance across your accounts.',
      },
      {
        icon: BarChart3,
        text: 'Execution: rebalance, then follow orders and trades as they go through.',
      },
    ],
    art: { main: BarChart3, left: Database, right: Briefcase },
  },
  {
    id: 'firm-forms',
    eyebrow: 'Settings',
    title: 'Upload your own firm KYC form',
    body: 'In Settings, the “Your own KYC forms” card takes your firm’s own PDF, the one you give every client.',
    points: [
      {
        icon: ScanText,
        text: 'We read the form and propose which client answer fills each box.',
      },
      {
        icon: CheckCircle2,
        text: 'You check a filled preview and approve it.',
      },
      {
        icon: FileText,
        text: 'From then on it is filled from each client’s onboarding answers and added to every account-opening package, next to the custodian’s forms.',
      },
    ],
    art: { main: FileText, left: ScanText, right: CheckCircle2 },
    action: { label: 'Open Settings', href: FIRM_FORMS_HREF },
  },
  {
    id: 'team',
    eyebrow: 'Settings',
    title: 'Make it yours',
    body: 'A few things worth setting before your first client.',
    points: [
      {
        icon: Users,
        text: 'Team & Access: invite your team and choose who is an admin.',
      },
      {
        icon: PenLine,
        text: 'Account: save your signature and your NBIN advisor code, so they are ready on every package.',
      },
      {
        icon: Palette,
        text: 'Appearance: light or dark, rounded or square. It follows you to any computer.',
      },
    ],
    art: { main: CheckCircle2, left: Users, right: Palette },
  },
];
